import { ICustomFieldConfig } from 'src/services/custom-fields/custom-field.types';

import { buildImportTargets } from 'src/sections/import/import-mapping';
import { ImportEntity, FULL_NAME_KEY, ImportFieldGroup, IMPORT_ENTITY_CONFIGS } from 'src/sections/import/import-fields';

/**
 * The columns a data export offers (JT1-1913). They are the importer's own targets, built by the
 * same function with the same labels, so an exported file's headings are exactly what the
 * importer matches on and the file goes back in without remapping. On top of those come a few
 * columns the importer has no field for, under `extra:` keys the server knows.
 */

export type ExportGroup = ImportFieldGroup | 'custom' | 'profile' | 'record';

export interface ExportTarget {
  /** The importer's field key, `cf:<config id>`, or an `extra:` key. */
  id: string;
  label: string;
  group: ExportGroup;
}

/**
 * Import targets an export leaves out: a full name is only ever split into first and last name,
 * and an owner id matches no one in another organisation, where the owner email still works.
 */
export const EXPORT_EXCLUDED_KEYS = [FULL_NAME_KEY, 'owner_id'];

/** Picker and file order: who the record is first, rarely needed columns last. */
export const EXPORT_GROUP_ORDER: ExportGroup[] = [
  'identity',
  'details',
  'company',
  'address',
  'availability',
  'organisation',
  'links',
  'custom',
  'profile',
  'record',
  'advanced',
];

interface ExtraField {
  key: string;
  labelKey: string;
  group: ExportGroup;
}

const RECORD_FIELDS: ExtraField[] = [
  { key: 'extra:id', labelKey: 'record_id', group: 'record' },
  { key: 'extra:created_at', labelKey: 'created_at', group: 'record' },
  { key: 'extra:updated_at', labelKey: 'updated_at', group: 'record' },
  { key: 'extra:lists', labelKey: 'lists', group: 'record' },
];

/** Not headed "Skills": the importer reads that heading as Tags. */
const CANDIDATE_PROFILE_FIELDS: ExtraField[] = [
  { key: 'extra:skills', labelKey: 'skills', group: 'profile' },
  { key: 'extra:current_job_title', labelKey: 'current_job_title', group: 'profile' },
  { key: 'extra:current_employer', labelKey: 'current_employer', group: 'profile' },
];

export const EXPORT_EXTRA_FIELDS: Record<ImportEntity, ExtraField[]> = {
  contact: RECORD_FIELDS,
  company: RECORD_FIELDS,
  candidate: [...CANDIDATE_PROFILE_FIELDS, ...RECORD_FIELDS],
};

type Translate = (key: string, options?: any) => string;

export function buildExportTargets(
  entity: ImportEntity,
  customFields: ICustomFieldConfig[] | undefined,
  t: Translate
): ExportTarget[] {
  const fromImport = buildImportTargets(IMPORT_ENTITY_CONFIGS[entity], customFields, t)
    .filter((target) => !EXPORT_EXCLUDED_KEYS.includes(target.id))
    .map(({ id, label, group }): ExportTarget => ({ id, label, group }));

  const extras = EXPORT_EXTRA_FIELDS[entity].map(
    (field): ExportTarget => ({ id: field.key, label: t(`data_export.fields.${field.labelKey}`), group: field.group })
  );

  const rank = (target: ExportTarget) => EXPORT_GROUP_ORDER.indexOf(target.group);

  // Array.prototype.sort is stable, so each group keeps the importer's field order.
  return [...fromImport, ...extras].sort((a, b) => rank(a) - rank(b));
}

/** Ticked until the user unticks it. Advanced holds ids that only help within one organisation. */
export const isDefaultSelected = (target: ExportTarget): boolean => target.group !== 'advanced';

/**
 * The labels of the fields the importer requires that are not selected, so the dialog can say the
 * file would not import back. An alternative built on the full name column cannot be met: that
 * column is never exported, since first and last name are.
 */
export function missingForReimport(entity: ImportEntity, selected: Set<string>, targets: ExportTarget[]): string[] {
  const labelOf = (key: string) => targets.find((target) => target.id === key)?.label ?? key;

  return IMPORT_ENTITY_CONFIGS[entity].requirements.flatMap((requirement) => {
    const usable = requirement.alternatives.filter((alternative) => !alternative.includes(FULL_NAME_KEY));
    if (usable.some((alternative) => alternative.every((key) => selected.has(key)))) return [];

    return (usable[0] ?? []).filter((key) => !selected.has(key)).map(labelOf);
  });
}
